import { MathExpression, Token, TokenType } from '@/api/dashboard'
import { MathTokenizer } from './tokenizer'
import { ParseResult } from './types'
import { Attribute } from '@/expr/types'

export class MathExpressionParser {
  private tokenizer: MathTokenizer

  constructor(expression: string) {
    this.tokenizer = new MathTokenizer(expression)
  }

  public static parse(expression: string): ParseResult {
    try {
      if (!expression || typeof expression !== 'string') {
        return {
          success: false,
        }
      }

      const parser = new MathExpressionParser(expression)
      return parser.parseExpression()
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown parsing error',
      }
    }
  }

  public static getTokensForView(tokens: Token[], attributes: Attribute[], eventAttributes: Attribute[]) {
    const newTokens: Token[] = []
    for (const token of tokens) {
      const newToken = { ...token }
      newTokens.push(newToken)
      const attribute = attributes.find(attr => attr.id === token.value)
      if (attribute) {
        newToken.value = attribute.name
      } else {
        const eventAttribute = eventAttributes.find(attr => attr.id === token.value)
        if (eventAttribute) {
          newToken.value = eventAttribute.name
        }
      }
    }
    return newTokens
  }

  public static stringify(expression: MathExpression, attributes: Attribute[], eventAttributes: Attribute[]): string {
    if (!expression || !expression.tokens || expression.tokens.length === 0) {
      return ''
    }
    let tokens = expression.tokens
    if (attributes?.length || eventAttributes?.length) {
      tokens = MathExpressionParser.getTokensForView(expression.tokens, attributes, eventAttributes)
    }

    return tokens
      .map(token => {
        if (token.type === TokenType.Operator) {
          // Convert operator codes back to symbols for readability
          const operatorMap: Record<string, string> = {
            '+': '+',
            '-': '-',
            '*': '*',
            '/': '/',
            '^': '^',
            '%': '%',
            '(': '(',
            ')': ')',
          }
          return operatorMap[token.value] || token.value
        }
        return token.value
      })
      .join(' ')
  }

  private parseExpression(): ParseResult {
    const validation = this.tokenizer.validate()
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error,
      }
    }

    try {
      // Reset tokenizer for actual parsing
      this.tokenizer = new MathTokenizer(this.tokenizer.expression)
      const tokens = this.tokenizer.tokenize()

      // Validate token sequence
      const tokenValidation = this.validateTokenSequence(tokens)
      if (!tokenValidation.isValid) {
        return {
          success: false,
          error: tokenValidation.error,
        }
      }

      const mathExpression: MathExpression = {
        name: '',
        tokens: tokens,
      }

      return {
        success: true,
        expression: mathExpression,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown parsing error',
      }
    }
  }

  private isValueToken(token: Token): boolean {
    return (
      token.type === TokenType.Number ||
      token.type === TokenType.Variable ||
      token.type === TokenType.Attribute ||
      token.type === TokenType.CustomAttribute ||
      token.type === TokenType.EventAttribute ||
      token.type === TokenType.CustomEventAttribute
    )
  }

  private validateTokenSequence(tokens: Token[]): { isValid: boolean; error?: string } {
    if (tokens.length === 0) {
      return {
        isValid: false,
        error: 'Empty expression',
      }
    }

    // Check for balanced parentheses
    let parenCount = 0
    for (const token of tokens) {
      if (token.value === '(') {
        parenCount++
      }
      if (token.value === ')') {
        parenCount--
      }
      if (parenCount < 0) {
        return {
          isValid: false,
          error: 'Mismatched parentheses - closing parenthesis without opening',
        }
      }
    }

    if (parenCount > 0) {
      return {
        isValid: false,
        error: 'Mismatched parentheses - unclosed opening parenthesis',
      }
    }

    // Check for proper operator placement
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]
      const prevToken = i > 0 ? tokens[i - 1] : null
      const nextToken = i < tokens.length - 1 ? tokens[i + 1] : null

      if (prevToken && this.isValueToken(prevToken) && this.isValueToken(token)) {
        return {
          isValid: false,
          error: `Invalid token sequence: ${prevToken.value} ${token.value}`,
        }
      }

      if (token.type === TokenType.Operator) {
        if (token.value === '(') {
          // Opening parenthesis can be at start or after operator
          if (nextToken && nextToken.value === ')') {
            return {
              isValid: false,
              error: 'Empty parentheses',
            }
          }
        } else if (token.value === ')') {
          // Closing parenthesis should have something before it
          if (!prevToken || prevToken.value === '(') {
            return {
              isValid: false,
              error: 'Empty parentheses',
            }
          }
        } else {
          // Determine if '-' is used as a unary minus
          const isUnaryMinus =
            token.value === '-' && (i === 0 || (prevToken?.type === TokenType.Operator && prevToken.value !== ')'))

          // Operators should not be at the beginning or end (except unary '-' at the beginning)
          if (i === tokens.length - 1) {
            return {
              isValid: false,
              error: `Operator '${token.value}' cannot be at beginning or end of expression`,
            }
          }
          if (!isUnaryMinus && i === 0) {
            return {
              isValid: false,
              error: `Operator '${token.value}' cannot be at beginning or end of expression`,
            }
          }

          // Check that a next token exists and is valid
          if (!nextToken) {
            return {
              isValid: false,
              error: `Operator '${token.value}' missing operands`,
            }
          }
          // Next token should be a value or opening paren
          if (nextToken.type === TokenType.Operator && nextToken.value !== '(') {
            return {
              isValid: false,
              error: `Invalid operator sequence: ${token.value} ${nextToken.value}`,
            }
          }

          // Previous token checks only apply for binary operators
          if (!isUnaryMinus) {
            if (!prevToken) {
              return {
                isValid: false,
                error: `Operator '${token.value}' missing operands`,
              }
            }
            // Previous token should be a value or closing paren
            if (prevToken.type === TokenType.Operator && prevToken.value !== ')') {
              return {
                isValid: false,
                error: `Invalid operator sequence: ${prevToken.value} ${token.value}`,
              }
            }
          }
        }
      }
    }

    return { isValid: true }
  }

  /**
   * Convenience method to parse and return only the MathExpression or throw an error
   * @param expression The mathematical expression as a string
   * @returns MathExpression object
   * @throws Error if parsing fails
   */
  public static parseOrThrow(expression: string): MathExpression {
    const result = MathExpressionParser.parse(expression)
    if (!result.success || !result.expression) {
      throw new Error(result.error || 'Failed to parse expression')
    }
    return result.expression
  }
}
